/**
 * The selected day at a glance: five headline numbers in one row, then one
 * line of secondary detail and, only when something failed, one line of
 * reasons. Only the two rates (success, battery per run) carry a comparison:
 * totals don't, because the bar strip above already shows how today stacks up
 * against the days around it, and a partial day would always read as "down".
 */
import PropTypes from "prop-types";
import { Icon } from "../../components/ui";
import {
  BASELINE_DAYS,
  baselineAverage,
  compare,
  formatCharge,
  formatCount,
  formatDistance,
  formatDuration,
  formatPct,
  formatRate,
  formatSpeed,
  reasonLabel,
} from "./robotTelemetry";

const TONE_COLORS = {
  good: "var(--bq-success)",
  bad: "var(--bq-danger)",
  neutral: "var(--bq-text3)",
};

// Change in percentage points: 0.018 -> "1.8 pts".
const points = (delta) => `${(Math.abs(delta) * 100).toFixed(1)} pts`;

const isNum = (n) => typeof n === "number" && Number.isFinite(n);

/** "1 error" / "14 errors"; null when the count wasn't reported. */
const countOf = (n, one, many) => {
  if (!isNum(n)) return null;
  const noun = n === 1 ? one : many;
  return `${formatCount(n)} ${noun}`;
};

const outcomeNote = (d) => {
  const misses = [d.aborted && `${d.aborted} aborted`, d.canceled && `${d.canceled} canceled`];
  const text = misses.filter(Boolean).join(", ");
  return text || "All succeeded";
};

/** Headline numbers. `trend` compares a rate with the recent average. */
export const HEADLINES = [
  { key: "runs", label: "Runs", value: (d) => formatCount(d.runs), note: outcomeNote },
  {
    key: "successRate",
    label: "Success rate",
    value: (d) => formatRate(d.successRate),
    // Under 0.05 pts reads as "0.0 pts", so it counts as no change.
    trend: { epsilon: 0.0005, format: points },
  },
  { key: "distanceM", label: "Distance", value: (d) => formatDistance(d.distanceM) },
  { key: "batteryUsedPct", label: "Battery used", value: (d) => formatCharge(d.batteryUsedPct) },
  {
    key: "avgBatteryPerRunPct",
    label: "Battery per run",
    value: (d) => formatPct(d.avgBatteryPerRunPct),
    trend: {
      higherIsBetter: false,
      epsilon: 0.05,
      format: (delta) => formatPct(Math.abs(delta)),
    },
  },
];

/** Staff-only numbers, shown inside the Navigation health disclosure. */
export const NAV_HEADLINES = [
  {
    key: "recoveries",
    label: "Recoveries",
    value: (d) => formatCount(d.recoveries),
    note: (d) => countOf(d.replans, "replan", "replans"),
  },
  {
    key: "avgPathEfficiency",
    label: "Path efficiency",
    value: (d) => formatRate(d.avgPathEfficiency),
    trend: { epsilon: 0.0005, format: points },
  },
  {
    key: "plannerFailures",
    label: "Planner failures",
    value: (d) => formatCount(d.plannerFailures),
    note: (d) => countOf(d.controllerFailures, "controller failure", "controller failures"),
  },
  { key: "stallEvents", label: "Stalls", value: (d) => formatCount(d.stallEvents) },
  {
    key: "localizationDegradedEvents",
    label: "Localisation lost",
    value: (d) => formatCount(d.localizationDegradedEvents),
    note: (d) => countOf(d.errors, "error", "errors"),
  },
];

const Trend = ({ metric, day, days }) => {
  const baseline = baselineAverage(days, day.date, metric.key);
  const result =
    baseline === null
      ? null
      : compare(day[metric.key], baseline, {
          higherIsBetter: metric.trend.higherIsBetter ?? true,
          epsilon: metric.trend.epsilon,
        });
  if (!result) return null;

  if (result.direction === "same") return <span>Same as {BASELINE_DAYS}-day avg</span>;
  const up = result.direction === "up";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        color: TONE_COLORS[result.tone],
      }}
    >
      <Icon name={up ? "arrow-up" : "arrow-down"} size={12} strokeWidth={2.25} />
      {up ? "Up" : "Down"} {metric.trend.format(result.delta)} vs {BASELINE_DAYS}-day avg
    </span>
  );
};

Trend.propTypes = {
  metric: PropTypes.object.isRequired,
  day: PropTypes.object.isRequired,
  days: PropTypes.array.isRequired,
};

/** A row of label-over-number stats. `size` steps the number down for secondary rows. */
export const StatRow = ({ metrics, day, days, size = "lg" }) => (
  <dl
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${size === "lg" ? 132 : 116}px, 1fr))`,
      gap: "14px 20px",
      margin: 0,
    }}
  >
    {metrics.map((m) => {
      const note = m.trend ? <Trend metric={m} day={day} days={days} /> : m.note?.(day);
      return (
        <div key={m.key} style={{ minWidth: 0 }}>
          <dt style={{ fontSize: 12, color: "var(--bq-text2)" }}>{m.label}</dt>
          <dd style={{ margin: 0 }}>
            <div
              style={{
                fontSize: size === "lg" ? 24 : 18,
                fontWeight: 700,
                lineHeight: 1.2,
                color: "var(--bq-text)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {m.value(day)}
            </div>
            {note && (
              <div style={{ fontSize: 11.5, color: "var(--bq-text3)", marginTop: 2 }}>{note}</div>
            )}
          </dd>
        </div>
      );
    })}
  </dl>
);

StatRow.propTypes = {
  metrics: PropTypes.array.isRequired,
  day: PropTypes.object.isRequired,
  days: PropTypes.array.isRequired,
  size: PropTypes.oneOf(["lg", "sm"]),
};

/** "2h 29m moving, 0.6 m/s average, 3.4% battery per km": whatever was reported. */
export const detailLine = (d) =>
  [
    isNum(d.movingTimeSec) && `${formatDuration(d.movingTimeSec)} moving`,
    isNum(d.avgSpeedMps) && `${formatSpeed(d.avgSpeedMps)} average`,
    isNum(d.batteryPerKmPct) && `${formatPct(d.batteryPerKmPct)} battery per km`,
  ]
    .filter(Boolean)
    .join(", ");

/** "No valid path (2), Goal blocked (1)"; null when every run succeeded. */
export const failureLine = (d) => {
  if (!d.failureReasons?.length) return null;
  return d.failureReasons.map((r) => `${reasonLabel(r.code)} (${r.count})`).join(", ");
};

const lineStyle = { margin: 0, fontSize: 12.5, color: "var(--bq-text2)", lineHeight: 1.5 };

const DaySummary = ({ day, days }) => {
  const detail = detailLine(day);
  const failures = failureLine(day);
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <StatRow metrics={HEADLINES} day={day} days={days} />
      {(detail || failures) && (
        <div style={{ display: "grid", gap: 4 }}>
          {detail && <p style={lineStyle}>{detail}</p>}
          {failures && (
            <p style={lineStyle}>
              <span style={{ color: "var(--bq-text)", fontWeight: 600 }}>Didn't succeed: </span>
              {failures}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

DaySummary.propTypes = {
  day: PropTypes.object.isRequired,
  days: PropTypes.array.isRequired,
};

export default DaySummary;
